
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Filter, X } from 'lucide-react';
import { FlightStatus } from '@/types/flight';

export interface FlightSearchCriteria {
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  flightDate: string;
  status: FlightStatus | 'all';
  dateFrom: string;
  dateTo: string;
}

interface FlightSearchFiltersProps {
  onSearch: (criteria: FlightSearchCriteria) => void;
  onClear: () => void;
  isSearching?: boolean;
}

export function FlightSearchFilters({ onSearch, onClear, isSearching = false }: FlightSearchFiltersProps) {
  const [filters, setFilters] = useState<FlightSearchCriteria>({
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    flightDate: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const handleFilterChange = (key: keyof FlightSearchCriteria, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    onSearch(filters);
  };

  const handleClear = () => {
    setFilters({
      flightNumber: '',
      departureAirport: '',
      arrivalAirport: '',
      flightDate: '',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
    onClear();
  };

  const hasActiveFilters = Object.values(filters).some(value => value && value !== 'all');

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4" />
          <h3 className="font-medium">Search & Filter Flights</h3>
        </div>
        
        {/* Prominent Flight Number Search */}
        <div className="mb-6">
          <label className="text-sm font-medium mb-2 block text-foreground">
            Search by Flight Number
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Enter flight number (e.g., PX392, PX224)"
                value={filters.flightNumber}
                onChange={(e) => handleFilterChange('flightNumber', e.target.value)}
                className="pl-10 text-lg h-12"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>
            <Button 
              onClick={handleSearch} 
              disabled={isSearching}
              size="lg"
              className="px-6"
            >
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Case-insensitive search with partial matching
          </p>
        </div>

        {/* Additional Filters (Collapsible) */}
        <div className="border-t pt-4">
          <p className="text-sm font-medium mb-3 text-foreground">Additional Filters (Optional)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Departure Airport</label>
            <Input
              placeholder="e.g., LAX, JFK"
              value={filters.departureAirport}
              onChange={(e) => handleFilterChange('departureAirport', e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Arrival Airport</label>
            <Input
              placeholder="e.g., SFO, ORD"
              value={filters.arrivalAirport}
              onChange={(e) => handleFilterChange('arrivalAirport', e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Flight Date</label>
            <Input
              type="date"
              value={filters.flightDate}
              onChange={(e) => handleFilterChange('flightDate', e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Status</label>
            <Select 
              value={filters.status} 
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="Check-in Open">Check-in Open</SelectItem>
                <SelectItem value="Check-in Closed">Check-in Closed</SelectItem>
                <SelectItem value="Boarding Soon">Boarding Soon</SelectItem>
                <SelectItem value="Boarding">Boarding</SelectItem>
                <SelectItem value="Gate Closed">Gate Closed</SelectItem>
                <SelectItem value="On Time">On Time</SelectItem>
                <SelectItem value="Delayed">Delayed</SelectItem>
                <SelectItem value="Departed">Departed</SelectItem>
                <SelectItem value="In Air">In Air</SelectItem>
                <SelectItem value="Diverted">Diverted</SelectItem>
                <SelectItem value="Recovery">Recovery</SelectItem>
                <SelectItem value="Landed">Landed</SelectItem>
                <SelectItem value="Arrived">Arrived</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Date From</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Date To</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <div className="text-sm text-muted-foreground">
            {hasActiveFilters && "Additional filters applied"}
          </div>
          
          <div className="flex gap-2">
            <Button 
              type="button"
              variant="outline" 
              onClick={handleClear}
              disabled={isSearching || !hasActiveFilters}
              size="sm"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All Filters
            </Button>
            
            <Button 
              type="button"
              onClick={handleSearch}
              disabled={isSearching}
              size="sm"
            >
              <Search className="h-4 w-4 mr-1" />
              {isSearching ? "Searching..." : "Apply Filters"}
            </Button>
          </div>
        </div>
      </div>
      </CardContent>
    </Card>
  );
}
